import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";
import { AlbumBrowseItemSchema } from "#shared/schemas/album.ts";
import { PlaylistBrowseItemSchema } from "#shared/schemas/playlist.ts";
import { TrackListSchema } from "#shared/schemas/trackQuery.ts";
import { z } from "zod";
import { cn } from "@web/lib/utils.ts";
import { api } from "@web/lib/api.ts";
import { Button } from "@web/components/ui/button.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { EnrichPanel } from "@web/features/settings/EnrichPanel.tsx";
import { LibraryStatusPanel } from "@web/features/settings/LibraryStatusPanel.tsx";
import { JsonTree } from "@web/features/settings/JsonTree.tsx";
import { ResearchPromptPanel } from "@web/features/workbench/ResearchPromptPanel.tsx";
import { EnrichSection } from "@web/features/detail/EnrichSection.tsx";
import { PlaylistEnrichSection } from "@web/features/detail/PlaylistEnrichSection.tsx";
import { ExportSection } from "@web/features/detail/ExportSection.tsx";
import { formatFact, presentFacts } from "@web/features/detail/facts.ts";
import { useEntityDetail } from "@web/features/detail/api.ts";
import { useArtists, useFieldDefs } from "@web/features/library/api.ts";

const AlbumBrowseResponse = z.object({ albums: z.array(AlbumBrowseItemSchema) });
const PlaylistBrowseResponse = z.object({
  playlists: z.array(PlaylistBrowseItemSchema),
});

const TABS = [
  { id: "sync", label: "Sync" },
  { id: "enrich", label: "Enrich" },
  { id: "inspect", label: "Entity inspect" },
  { id: "prompt", label: "Prompt" },
] as const;

type TabId = (typeof TABS)[number]["id"];

type InspectKind = "track" | "album" | "artist" | "playlist";

type InspectHit = {
  kind: InspectKind;
  id: string;
  label: string;
  sub?: string | null;
};

function isTab(value: string | null): value is TabId {
  return TABS.some((t) => t.id === value);
}

function isInspectKind(value: string | null): value is InspectKind {
  return value === "track" || value === "album" || value === "artist" || value === "playlist";
}

/** Spotify IDs are 22 base62 chars; also accept URI / open.spotify.com URLs. */
function extractSpotifyId(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const fromUri = trimmed.match(
    /(?:spotify:)?(?:track|album|artist|playlist)[:/]([A-Za-z0-9]{22})\b/i,
  );
  if (fromUri?.[1]) return fromUri[1];
  const fromUrl = trimmed.match(
    /open\.spotify\.com\/(?:intl-[a-z]+\/)?(?:track|album|artist|playlist)\/([A-Za-z0-9]{22})\b/i,
  );
  if (fromUrl?.[1]) return fromUrl[1];
  if (/^[A-Za-z0-9]{22}$/.test(trimmed)) return trimmed;
  return null;
}

/**
 * Admin / workbench mode — enrichment, sync health, MBID/cascade inspect.
 * No sidebar entry; open via /workbench or Settings link.
 */
export function WorkbenchPage() {
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab");
  const tab: TabId = isTab(raw) ? raw : "sync";

  const blurb = useMemo(() => {
    if (tab === "enrich") {
      return "Coverage, batch enrich, force refetch, resume — all technical tooling.";
    }
    if (tab === "inspect") {
      return "Find by Spotify ID or name — full entity detail + MB/AB cascade in one place.";
    }
    if (tab === "prompt") {
      return "Initial + continue clipboard templates (admin) and per-user category angles.";
    }
    return "Mirror size, sync jobs, Spotify API health, rate limits, cover status.";
  }, [tab]);

  function select(next: TabId) {
    const copy = new URLSearchParams(params);
    if (next === "sync") {
      copy.delete("tab");
    } else {
      copy.set("tab", next);
    }
    if (next !== "enrich") {
      copy.delete("playlist");
    }
    if (next !== "inspect") {
      copy.delete("entity_type");
      copy.delete("entity_id");
    }
    setParams(copy, { replace: true });
  }

  return (
    <div className="flex h-full flex-col">
      <header className="shrink-0 border-b border-line px-5 pt-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold tracking-wider text-faint uppercase">Admin</p>
            <h1 className="text-2xl font-semibold tracking-tight">Workbench</h1>
            <p className="mt-1 text-sm text-muted">{blurb}</p>
          </div>
          <Button variant="secondary" size="sm" asChild>
            <Link to="/settings">Back to Settings</Link>
          </Button>
        </div>

        <div role="tablist" aria-label="Workbench sections" className="mt-4 flex gap-1">
          {TABS.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              id={`workbench-tab-${item.id}`}
              aria-controls={`workbench-panel-${item.id}`}
              className={cn(
                "-mb-px border-b-2 px-3 py-2 text-sm font-semibold",
                tab === item.id
                  ? "border-accent text-text"
                  : "border-transparent text-muted hover:text-text",
              )}
              onClick={() => select(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto p-5">
        <div
          className={cn(
            "mx-auto",
            tab === "enrich" || tab === "inspect" || tab === "prompt" ? "max-w-4xl" : "max-w-3xl",
          )}
          role="tabpanel"
          id={`workbench-panel-${tab}`}
          aria-labelledby={`workbench-tab-${tab}`}
        >
          {tab === "sync" ? (
            <LibraryStatusPanel />
          ) : tab === "enrich" ? (
            <EnrichPanel />
          ) : tab === "prompt" ? (
            <ResearchPromptPanel />
          ) : (
            <EntityInspectPanel />
          )}
        </div>
      </div>
    </div>
  );
}

function EntityInspectPanel() {
  const [params, setParams] = useSearchParams();
  const urlType = params.get("entity_type");
  const urlId = params.get("entity_id");

  const [kind, setKind] = useState<InspectKind>(isInspectKind(urlType) ? urlType : "track");
  const [query, setQuery] = useState(urlId ?? "");
  const [searchTerm, setSearchTerm] = useState("");
  const [active, setActive] = useState<{ kind: InspectKind; id: string } | null>(
    isInspectKind(urlType) && urlId ? { kind: urlType, id: urlId } : null,
  );

  const spotifyId = extractSpotifyId(searchTerm);
  const nameTerm = spotifyId ? "" : searchTerm.trim();
  const searching = nameTerm.length > 0;

  const trackHits = useQuery({
    queryKey: ["workbench-inspect-tracks", nameTerm],
    enabled: searching && kind === "track",
    queryFn: async (): Promise<InspectHit[]> => {
      const sp = new URLSearchParams({
        scope: "all",
        q: nameTerm,
        limit: "20",
      });
      const body = TrackListSchema.parse(await api(`/api/tracks?${sp}`));
      return body.rows.map((row) => ({
        kind: "track" as const,
        id: row.id,
        label: row.name,
        sub: row.artists.map((a) => a.name ?? a.id).join(", ") || null,
      }));
    },
  });

  const albumHits = useQuery({
    queryKey: ["workbench-inspect-albums", nameTerm],
    enabled: searching && kind === "album",
    queryFn: async (): Promise<InspectHit[]> => {
      const sp = new URLSearchParams({ q: nameTerm });
      const body = AlbumBrowseResponse.parse(await api(`/api/albums?${sp}`));
      return body.albums.slice(0, 20).map((a) => ({
        kind: "album" as const,
        id: a.id,
        label: a.name ?? a.id,
        sub:
          a.artists.map((x) => x.name ?? x.id).join(", ") ||
          (a.release_year != null ? String(a.release_year) : null),
      }));
    },
  });

  const artistsQ = useArtists();

  const playlistHits = useQuery({
    queryKey: ["workbench-inspect-playlists", nameTerm],
    enabled: searching && kind === "playlist",
    queryFn: async (): Promise<InspectHit[]> => {
      const sp = new URLSearchParams({ q: nameTerm });
      const body = PlaylistBrowseResponse.parse(await api(`/api/playlists/browse?${sp}`));
      return body.playlists.slice(0, 20).map((p) => ({
        kind: "playlist" as const,
        id: p.id,
        label: p.name,
        sub: `${p.tracks_in_library} tracks` + (p.owner_name ? ` · ${p.owner_name}` : ""),
      }));
    },
  });

  const hits: InspectHit[] = useMemo(() => {
    if (!searching) return [];
    if (kind === "track") return trackHits.data ?? [];
    if (kind === "album") return albumHits.data ?? [];
    if (kind === "playlist") return playlistHits.data ?? [];
    const needle = nameTerm.toLowerCase();
    return (artistsQ.data ?? [])
      .filter((a) => (a.name ?? "").toLowerCase().includes(needle))
      .slice(0, 20)
      .map((a) => ({
        kind: "artist" as const,
        id: a.id,
        label: a.name ?? a.id,
        sub: `${a.track_count} tracks · ${a.album_count} albums`,
      }));
  }, [
    searching,
    kind,
    trackHits.data,
    albumHits.data,
    playlistHits.data,
    artistsQ.data,
    nameTerm,
  ]);

  const hitsLoading =
    searching &&
    ((kind === "track" && trackHits.isFetching) ||
      (kind === "album" && albumHits.isFetching) ||
      (kind === "artist" && artistsQ.isLoading) ||
      (kind === "playlist" && playlistHits.isFetching));

  function openInspect(next: { kind: InspectKind; id: string }) {
    setActive(next);
    setKind(next.kind);
    setQuery(next.id);
    setSearchTerm("");
    const copy = new URLSearchParams(params);
    copy.set("tab", "inspect");
    copy.set("entity_type", next.kind);
    copy.set("entity_id", next.id);
    setParams(copy, { replace: true });
  }

  function runSearch() {
    const raw = query.trim();
    if (!raw) return;
    const id = extractSpotifyId(raw);
    if (id) {
      openInspect({ kind, id });
      return;
    }
    setSearchTerm(raw);
  }

  return (
    <div className="space-y-5">
      <section className="space-y-3 rounded-lg border border-line bg-panel px-4 py-4">
        <h2 className="text-sm font-semibold">Open entity</h2>
        <p className="text-xs text-muted">
          Paste a Spotify ID / URI / URL, or type a name to search the local mirror.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <label className="space-y-1 text-xs text-muted">
            Type
            <select
              className="block h-9 rounded-md border border-line bg-panel-elevated px-2 text-sm text-text"
              value={kind}
              onChange={(e) => {
                setKind(e.target.value as InspectKind);
                setSearchTerm("");
              }}
            >
              <option value="track">Track</option>
              <option value="album">Album</option>
              <option value="artist">Artist</option>
              <option value="playlist">Playlist</option>
            </select>
          </label>
          <label className="min-w-48 flex-1 space-y-1 text-xs text-muted">
            ID or name
            <input
              type="text"
              value={query}
              spellCheck={false}
              placeholder="Spotify id, URI, URL, or name…"
              className="block h-9 w-full rounded-md border border-line bg-panel-elevated px-2 text-sm text-text placeholder:text-faint"
              onChange={(e) => {
                setQuery(e.target.value);
                setSearchTerm("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") runSearch();
              }}
            />
          </label>
          <Button type="button" size="sm" disabled={!query.trim()} onClick={runSearch}>
            {extractSpotifyId(query) ? "Inspect" : "Search"}
          </Button>
        </div>

        {searching && (
          <div className="space-y-1 border-t border-line pt-3">
            <p className="text-xs font-semibold tracking-wider text-faint uppercase">
              Matches for “{nameTerm}”
            </p>
            {hitsLoading && <Skeleton className="h-16 w-full" />}
            {!hitsLoading && hits.length === 0 && (
              <p className="text-sm text-muted">No matches in the local library.</p>
            )}
            {!hitsLoading && hits.length > 0 && (
              <ul className="divide-y divide-line rounded-md border border-line">
                {hits.map((hit) => (
                  <li key={`${hit.kind}-${hit.id}`}>
                    <button
                      type="button"
                      className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-row-hover"
                      onClick={() => openInspect(hit)}
                    >
                      <span className="text-sm font-medium text-text">{hit.label}</span>
                      <span className="font-mono text-xs text-faint">{hit.id}</span>
                      {hit.sub && <span className="text-xs text-muted">{hit.sub}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      {active ? <CombinedInspect kind={active.kind} id={active.id} /> : (
        <p className="text-sm text-muted">No entity loaded yet.</p>
      )}
    </div>
  );
}

/** Entity detail (facts, genres, providers, values, export) + enrich tooling. */
function CombinedInspect({ kind, id }: { kind: InspectKind; id: string }) {
  const detailQ = useEntityDetail({ type: kind, id });
  const fieldsQ = useFieldDefs();
  const detail = detailQ.data;

  return (
    <div className="space-y-6">
      {detailQ.isLoading && <Skeleton className="h-40 w-full" />}
      {detailQ.isError && (
        <p className="text-sm text-destructive">
          Could not load entity detail for {kind}/{id}. ID may be wrong or not in the mirror.
        </p>
      )}

      {detail && (
        <section className="space-y-4 rounded-lg border border-line bg-panel px-4 py-4">
          <div className="flex gap-4">
            {detail.image_url ? (
              <img
                src={detail.image_url}
                alt=""
                className="size-24 shrink-0 rounded-md object-cover"
              />
            ) : (
              <div className="size-24 shrink-0 rounded-md bg-panel-elevated" />
            )}
            <div className="min-w-0 space-y-1">
              <p className="text-xs font-semibold tracking-wider text-faint uppercase">
                {detail.entity_type}
              </p>
              <h2 className="text-xl font-semibold tracking-tight">{detail.title}</h2>
              {detail.subtitle && <p className="text-sm text-muted">{detail.subtitle}</p>}
              <p className="font-mono text-xs text-faint">{detail.entity_id}</p>
              {detail.external_url && (
                <a
                  href={detail.external_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block text-xs text-accent hover:underline"
                >
                  Open in Spotify
                </a>
              )}
            </div>
          </div>

          {presentFacts(detail.facts).length > 0 && (
            <div>
              <h3 className="mb-2 text-xs font-semibold tracking-wider text-faint uppercase">
                Facts
              </h3>
              <dl className="divide-y divide-line rounded-md border border-line">
                {presentFacts(detail.facts).map((fact) => (
                  <div
                    key={`${fact.label}-${formatFact(fact)}`}
                    className="flex justify-between gap-3 px-3 py-2 text-sm"
                  >
                    <dt className="text-muted">{fact.label}</dt>
                    <dd className="text-right text-text">{formatFact(fact)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {detail.status && (
            <div>
              <h3 className="mb-2 text-xs font-semibold tracking-wider text-faint uppercase">
                Status
              </h3>
              <dl className="space-y-1 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-muted">Status</dt>
                  <dd>{detail.status.status}</dd>
                </div>
                {detail.status.follow_up && (
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted">Follow-up</dt>
                    <dd className="text-right">{detail.status.follow_up}</dd>
                  </div>
                )}
                {detail.status.drive_url && (
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted">Drive</dt>
                    <dd className="truncate text-right">
                      <a
                        href={detail.status.drive_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-accent hover:underline"
                      >
                        {detail.status.drive_url}
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}

          {detail.genres.length > 0 && (
            <div>
              <h3 className="mb-2 text-xs font-semibold tracking-wider text-faint uppercase">
                Genres
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {detail.genres.map((g) => (
                  <span
                    key={`${g.source}-${g.genre_id}`}
                    className="rounded-full bg-panel-elevated px-2 py-1 text-xs text-muted"
                    title={g.source}
                  >
                    {g.name}
                    <span className="ml-1 text-faint">({g.source})</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {detail.providers.map((block) => (
            <div key={`${block.label}-${block.source}`}>
              <h3 className="mb-2 text-xs font-semibold tracking-wider text-faint uppercase">
                {block.label}
                <span className="ml-2 font-normal normal-case tracking-normal text-faint">
                  {block.source}
                </span>
              </h3>
              <dl className="divide-y divide-line rounded-md border border-line">
                {presentFacts(block.facts).map((fact) => (
                  <div
                    key={`${block.source}-${fact.label}`}
                    className="flex justify-between gap-3 px-3 py-2 text-sm"
                  >
                    <dt className="text-muted">{fact.label}</dt>
                    <dd className="text-right text-text">{formatFact(fact)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}

          {Object.keys(detail.values).length > 0 && (
            <div>
              <h3 className="mb-2 text-xs font-semibold tracking-wider text-faint uppercase">
                Custom field values
              </h3>
              <JsonTree value={detail.values} />
            </div>
          )}

          {fieldsQ.data && (
            <div>
              <h3 className="mb-2 text-xs font-semibold tracking-wider text-faint uppercase">
                Export
              </h3>
              <ExportSection detail={detail} defs={fieldsQ.data} />
            </div>
          )}
        </section>
      )}

      <section className="space-y-3">
        <h2 className="text-sm font-semibold tracking-tight">Enrich / cascade</h2>
        {kind === "playlist" ? (
          <PlaylistEnrichSection playlistId={id} />
        ) : (
          <EnrichSection entityType={kind} entityId={id} />
        )}
      </section>
    </div>
  );
}
